import { eq } from "drizzle-orm";

import { createLogger } from "@/logger";

const log = createLogger("feed");

import { client } from "@/config";
import { db } from "@/db/db";
import { feed } from "@/db/schema";
import type { Feed } from "@/db/types";
import { bulkUpsertItemAndCalculateMembership } from "@/models/repositories/Item";
import { emitTableUpdates } from "../eventing/TableEventManager";

/**
 * Fetches feed items based on direction (newer or older) from a reference point
 *
 * @param workspaceId The workspace ID
 * @param feedId The feed ID
 * @param direction Whether to fetch newer or older items
 * @param pageSize Number of items to fetch
 * @param referenceTimestamp Optional timestamp to use as reference for pagination
 * @param referenceItemId Optional item ID to use as reference for pagination
 * @returns The fetched items
 */
export async function fetchFeedItems({
  workspaceId,
  feedId,
  direction,
  pageSize = 200,
  referenceTimestamp,
  referenceItemId,
}: {
  workspaceId: string;
  feedId: string;
  direction: "newer" | "older";
  pageSize?: number;
  referenceTimestamp?: string;
  referenceItemId?: string;
}) {
  log.debug("Fetching feed items:", {
    workspaceId,
    feedId,
    direction,
    pageSize,
    referenceTimestamp,
    referenceItemId,
  });

  try {
    // When fetching "newer" items, we want to sort in ascending order
    // When fetching "older" items, we want to sort in descending order
    const sortAscending = direction === "newer";

    // If we have an item ID reference but no timestamp, fetch items around that ID
    if (referenceItemId) {
      const result = await client.bootstrapWorkspaceFeedItemsPaginated({
        workspaceId,
        feedId,
        pageSize,
        sortAscending,
        itemId: referenceItemId,
        useCreatedAt: true,
      });

      // Save the fetched items to the database
      if (result?.items?.length > 0) {
        await bulkUpsertItemAndCalculateMembership(result.items);
      }

      // Update the feed's first/last page loaded flags if needed
      let feedUpdated = false;
      if (direction === "newer" && result?.items?.length < pageSize) {
        const currentFeed = await db.select().from(feed).where(eq(feed.id, feedId)).limit(1);
        if (currentFeed[0] && !currentFeed[0].loadedLastPage) {
          await db
            .update(feed)
            .set({ loadedLastPage: true } as Partial<Feed>)
            .where(eq(feed.id, feedId))
            .execute();
          feedUpdated = true;
        }
      } else if (direction === "older" && result?.items?.length < pageSize) {
        const currentFeed = await db.select().from(feed).where(eq(feed.id, feedId)).limit(1);
        if (currentFeed[0] && !currentFeed[0].loadedFirstPage) {
          await db
            .update(feed)
            .set({ loadedFirstPage: true } as Partial<Feed>)
            .where(eq(feed.id, feedId))
            .execute();
          feedUpdated = true;
        }
      }

      if (feedUpdated) {
        emitTableUpdates(["feed"]);
      }
      return result;
    }

    const result = await client.bootstrapWorkspaceFeedItemsPaginated({
      workspaceId,
      feedId,
      pageSize,
      timestamp: referenceTimestamp,
      sortAscending,
      useCreatedAt: true,
    });

    // Save the fetched items to the database
    if (result?.items?.length > 0) {
      await bulkUpsertItemAndCalculateMembership(result.items);
    }

    // Update the feed's first/last page loaded flags if needed
    let feedUpdated = false;
    if (direction === "newer" && result?.items?.length < pageSize) {
      const currentFeed = await db.select().from(feed).where(eq(feed.id, feedId)).limit(1);
      if (currentFeed[0] && !currentFeed[0].loadedLastPage) {
        await db
          .update(feed)
          .set({ loadedLastPage: true } as Partial<Feed>)
          .where(eq(feed.id, feedId))
          .execute();
        feedUpdated = true;
      }
    } else if (direction === "older" && result?.items?.length < pageSize) {
      const currentFeed = await db.select().from(feed).where(eq(feed.id, feedId)).limit(1);
      const updates: Partial<Feed> = {};
      if (currentFeed[0] && !currentFeed[0].loadedFirstPage) {
        updates.loadedFirstPage = true;
      }
      if (!referenceTimestamp && currentFeed[0] && !currentFeed[0].loadedLastPage) {
        updates.loadedLastPage = true;
      }
      if (Object.keys(updates).length > 0) {
        await db.update(feed).set(updates).where(eq(feed.id, feedId)).execute();
        feedUpdated = true;
      }
    }

    if (feedUpdated) {
      emitTableUpdates(["feed"]);
    }
    return result;
  } catch (error) {
    log.error("Error fetching feed items:", error);
    // Return empty result on error
    return { items: [], pageSize };
  }
}
